/**
 * Lignes — une liste de rangées sans trait de séparation (lot « surfaces », 01-11-2026) : le
 * planning de la semaine (« Lundi · Repos »), les derniers shifts, une liste clé-valeur, une liste
 * où l'on choisit. Chaque `Ligne` : quelque chose avant (icône, avatar), l'étiquette et un
 * sous-texte, la valeur à droite, quelque chose après (chevron, bouton). Cliquable = un vrai
 * bouton (ou lien) ; `actif` = la rangée choisie, voilée à la marque — la marque sur ce qui est CHOISI.
 *
 *   <Lignes>
 *     <Ligne etiquette="Lundi" valeur="Repos" />
 *     <Ligne etiquette="Mardi" valeur="20:00 – 02:00" actif onClick={…} />
 *   </Lignes>
 */
import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ReactNode } from "react";

import { cn } from "./utils";
import "./surfaces.css";

export interface LignesProps {
  children: ReactNode;
  ariaLabel?: string;
  className?: string;
}

export function Lignes({ children, ariaLabel, className }: LignesProps) {
  return (
    <ul className={cn("sf-lignes", className)} aria-label={ariaLabel}>
      {children}
    </ul>
  );
}

export interface LigneProps {
  etiquette: ReactNode;
  /** Sous l'étiquette, gris. */
  sous?: ReactNode;
  /** À droite, en chiffres alignés. */
  valeur?: ReactNode;
  /** Avant l'étiquette : une icône, un avatar. */
  avant?: ReactNode;
  /** Après la valeur : un chevron, un bouton. */
  apres?: ReactNode;
  /** La rangée choisie. */
  actif?: boolean;
  /** Grisée. */
  eteint?: boolean;
  onClick?: ButtonHTMLAttributes<HTMLButtonElement>["onClick"];
  href?: string;
  target?: AnchorHTMLAttributes<HTMLAnchorElement>["target"];
  title?: string;
  className?: string;
}

export function Ligne({ etiquette, sous, valeur, avant, apres, actif = false, eteint = false, onClick, href, target, title, className }: LigneProps) {
  const contenu = (
    <>
      {avant !== undefined && avant !== null && <span className="sf-ligne-avant">{avant}</span>}
      <span className="sf-ligne-texte">
        <span className="sf-ligne-etiquette">{etiquette}</span>
        {sous !== undefined && sous !== null && <span className="sf-ligne-sous">{sous}</span>}
      </span>
      {valeur !== undefined && valeur !== null && <span className="sf-ligne-valeur">{valeur}</span>}
      {apres !== undefined && apres !== null && <span className="sf-ligne-apres">{apres}</span>}
    </>
  );
  const commun = {
    className: cn("sf-ligne sf-arrondi-ligne", className),
    "data-actif": actif || undefined,
    "data-eteint": eteint || undefined,
    title,
  };
  return (
    <li aria-current={actif ? "true" : undefined}>
      {href !== undefined ? (
        <a href={href} target={target} rel={target === "_blank" ? "noopener noreferrer" : undefined} {...commun} data-cliquable="">
          {contenu}
        </a>
      ) : onClick ? (
        <button type="button" onClick={onClick} {...commun} data-cliquable="">
          {contenu}
        </button>
      ) : (
        <div {...commun}>{contenu}</div>
      )}
    </li>
  );
}
